import { inject, Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular';

import { environment } from '@app-env/environment';
import { FunctionsService } from '../functions/functions.service';

/**
 * Result of the merged `intercom_sync_v4` callable — re-derive the contact's
 * attributes from Firestore and, when a product is supplied, recompute the
 * rolled-up person_status and push it (plus the raw product state) to Intercom.
 * The browser fires this fire-and-forget and never inspects the body; the shape
 * is declared so the callable is typed.
 */
export type IntercomSyncPersonStatusResponse = {
  success: boolean;
  status: string;
  person_status?: string | null;
  intercom_id?: string | null;
  audit_log_id?: string | null;
  error?: string | null;
};

type IntercomFn = ((...args: unknown[]) => void) & {
  q?: unknown[][];
  c?: (args: unknown[]) => void;
};

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions -- declare-global augmentation requires `interface`.
  interface Window {
    Intercom?: IntercomFn;
    intercomSettings?: Record<string, unknown>;
  }
}

const SNIPPET_BASE_URL = 'https://widget.intercom.io/widget/';

/**
 * Identity/financial fields that must never be sent to Intercom under the
 * compliance rules. The producer allow-list already excludes these, but
 * `identify` accepts an arbitrary attributes object, so we strip them here too
 * as a backstop. Matched case-insensitively as substrings of the key.
 */
const SENSITIVE_KEY_PATTERNS = [
  'ird',
  'pir',
  'passport',
  'license',
  'licence',
  'dob',
  'date_of_birth',
  'dateofbirth',
  'address',
  'tax',
  'balance',
];

function stripSensitiveKeys(attributes: Record<string, unknown>): Record<string, unknown> {
  const safe: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(attributes)) {
    const lower = key.toLowerCase();
    if (!SENSITIVE_KEY_PATTERNS.some((pattern) => lower.includes(pattern))) {
      safe[key] = value;
    }
  }
  return safe;
}

@Injectable({
  providedIn: 'root',
})
export class IntercomService {
  private readonly functionsService = inject(FunctionsService);

  private booted = false;
  private scriptInjected = false;

  /**
   * Boot Intercom so the launcher bubble appears, without opening the messenger.
   * Booted as an anonymous lead at the start of onboarding so the live-chat
   * widget is available before the user signs up, then re-booted post-auth.
   * Idempotent, so the onboarding boot and post-auth boot never double-inject.
   *
   * Boot is attribute-free. Contact attributes are written server-side via
   * {@link sync}; the lead is associated with the authenticated user via
   * {@link identify}.
   */
  boot(): void {
    this.ensureBooted();
  }

  /**
   * Open the Intercom messenger. Lazily boots on first call so the widget script
   * isn't downloaded until the user engages with chat.
   */
  show(): void {
    this.ensureBooted();
    this.call('show');
  }

  /**
   * Open the messenger directly to the new-message composer, optionally
   * pre-filled with `message`. Use this when the surrounding UI implies the
   * user wants to ask a question (e.g. an "Any more questions?" affordance).
   */
  showNewMessage(message?: string): void {
    this.ensureBooted();
    if (message) {
      this.call('showNewMessage', message);
    } else {
      this.call('showNewMessage');
    }
  }

  /** Close the messenger. No-op when not yet booted. */
  hide(): void {
    if (!this.booted) {
      return;
    }
    this.call('hide');
  }

  /**
   * Associate the current anonymous lead with an authenticated user. Intercom
   * uses `user_id` to merge the pre-auth lead (and its onboarding attributes)
   * into the authenticated user rather than creating a duplicate contact.
   *
   * No-op when not yet booted. Identity/financial keys in `attributes` are
   * stripped as a compliance backstop; `user_id` is always kept.
   */
  identify(userId: string, attributes?: Record<string, unknown>): void {
    if (!this.booted) {
      return;
    }
    const safeAttributes = attributes ? stripSensitiveKeys(attributes) : {};
    this.call('update', { user_id: userId, ...safeAttributes });
  }

  /**
   * Merged CRM sync via the intercom_sync_v4 callable. Re-derives the person's
   * Intercom attributes from Firestore and, when a productId is supplied, also
   * pushes the recomputed "Person Status" and the raw "Kiwisaver Product Status".
   * Authorized for the person themselves (onboarding) or an admin, so it serves
   * both the onboarding flow and the admin member page. Once a lead is identified
   * (post-signup) the browser Messenger 403s attribute writes, so identified users
   * must sync server-side via this method.
   *
   * Fire-and-forget: errors are captured to Sentry and a failed response is
   * returned. This method never throws. No-op on an empty/whitespace `personId` —
   * early onboarding navigation can fire this before the person id is set, and
   * hitting the callable with an empty id would fail server-side auth/lookup and
   * emit a Sentry error for a benign no-op.
   */
  async sync(personId: string, productId?: string): Promise<IntercomSyncPersonStatusResponse> {
    if (!personId?.trim()) {
      return { success: false, status: 'skipped' };
    }

    const callable = this.functionsService.callable<
      { person_id: string; product_id?: string },
      IntercomSyncPersonStatusResponse
    >('intercom_sync_v4');

    try {
      const payload = productId
        ? { person_id: personId, product_id: productId }
        : { person_id: personId };
      const result = await callable(payload);
      return result.data;
    } catch (e) {
      Sentry.captureException(e, {
        tags: { service: 'intercom', operation: 'sync' },
        extra: { personId, productId },
      });
      return { success: false, status: 'failed', error: String(e) };
    }
  }

  /**
   * End the current Intercom session. Call from the logout flow so the next
   * user on the same browser starts a fresh session and we don't leak PII
   * between users.
   */
  shutdown(): void {
    if (!this.booted) {
      return;
    }
    this.call('shutdown');
    this.booted = false;
  }

  private ensureBooted(): void {
    if (this.booted) {
      return;
    }
    const appId = environment.intercom?.id;
    if (!appId) {
      return;
    }
    if (typeof window === 'undefined') {
      return;
    }
    try {
      this.injectSnippet(appId);
      this.call('boot', { app_id: appId });
      this.booted = true;
    } catch (e) {
      Sentry.captureException(e, { tags: { service: 'intercom', operation: 'boot' } });
    }
  }

  /**
   * Install the queueing function on `window.Intercom` and inject the widget
   * script. Calls made before the script finishes loading are queued and
   * replayed by Intercom's real implementation once it's ready.
   */
  private injectSnippet(appId: string): void {
    if (this.scriptInjected || window.Intercom) {
      this.scriptInjected = true;
      return;
    }

    const queue: IntercomFn = function (...args: unknown[]): void {
      queue.c?.(args);
    } as IntercomFn;
    queue.q = [];
    queue.c = (args: unknown[]): void => {
      queue.q!.push(args);
    };
    window.Intercom = queue;

    const script = document.createElement('script');
    script.async = true;
    script.src = `${SNIPPET_BASE_URL}${appId}`;
    document.head.appendChild(script);
    this.scriptInjected = true;
  }

  private call(...args: unknown[]): void {
    try {
      window.Intercom?.(...args);
    } catch (e) {
      Sentry.captureException(e, {
        tags: { service: 'intercom', operation: String(args[0] ?? 'unknown') },
      });
    }
  }
}
